<template>
    <div>
        <v-tooltip text="Phone Recordings" location="bottom">
            <template v-slot:activator="{ props }">
                <v-btn variant="plain" icon="mdi-phone-log" v-bind="props" @click="open"></v-btn>
            </template>
        </v-tooltip>

        <v-dialog v-model="show" fullscreen persistent transition="dialog-bottom-transition" :scrim="false">
            <v-card>
                <v-toolbar dark color="primary">
                    <v-toolbar-title>Phone Recordings</v-toolbar-title>
                    <v-spacer></v-spacer>
                    <v-btn icon dark @click="show = false">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-toolbar>

                <v-card-text>
                    <v-row dense class="mt-1">
                        <v-col cols="12" sm="3">
                            <v-text-field v-model="startDate" label="From" type="date" hide-details></v-text-field>
                        </v-col>
                        <v-col cols="12" sm="3">
                            <v-text-field v-model="endDate" label="To" type="date" hide-details></v-text-field>
                        </v-col>
                        <v-col cols="12" sm="4">
                            <v-text-field v-model="searchTerm"
                                          label="Search name or number"
                                          clearable
                                          prepend-inner-icon="mdi-magnify"
                                          hide-details
                                          @keyup.enter="loadData"
                                          @click:clear="onClearSearch"></v-text-field>
                        </v-col>
                        <v-col cols="12" sm="2" class="d-flex align-center mt-2">
                            <v-btn @click="loadData" :loading="loading" prepend-icon="mdi-magnify" block>Search</v-btn>
                        </v-col>
                    </v-row>

                    <v-progress-linear v-if="loading" indeterminate color="primary" class="mt-4"></v-progress-linear>

                    <div v-else-if="recordings.length === 0" class="text-grey mt-6 text-center">
                        No recordings found for the selected period.
                    </div>

                    <v-row class="mt-3" v-else>
                        <v-col cols="12" md="6" lg="4" v-for="rec in recordings" :key="rec.id">
                            <v-card variant="outlined">
                                <v-card-text class="pb-0">
                                    <div class="d-flex align-center mb-2">
                                        <div class="flex-grow-1 min-width-0">
                                            <div class="font-weight-bold text-truncate">{{ rec.callerName || rec.callerNumber }}</div>
                                            <div class="text-caption text-grey" v-if="rec.callerName">{{ rec.callerNumber }}</div>
                                        </div>
                                        <v-icon icon="mdi-arrow-right-thin" class="mx-2 text-grey flex-shrink-0"></v-icon>
                                        <div class="text-right flex-grow-1 min-width-0">
                                            <div class="font-weight-bold text-truncate">{{ rec.destinationName || rec.destinationNumber }}</div>
                                            <div class="text-caption text-grey" v-if="rec.destinationName">{{ rec.destinationNumber }}</div>
                                        </div>
                                    </div>
                                    <div class="d-flex flex-wrap gap-x-4 text-caption text-grey">
                                        <span>
                                            <v-icon size="x-small" class="mr-1">mdi-calendar</v-icon>{{ $dayjs(rec.startTime).format('DD/MM/YY HH:mm') }}
                                        </span>
                                        <span>
                                            <v-icon size="x-small" class="mr-1">mdi-clock-outline</v-icon>{{ formatDuration(rec.duration) }}
                                        </span>
                                        <span v-if="rec.contactName">
                                            <v-icon size="x-small" class="mr-1">mdi-account</v-icon>{{ rec.contactName }}
                                        </span>
                                    </div>
                                </v-card-text>
                                <v-card-actions>
                                    <v-btn v-if="rec.downloadUrl"
                                           :href="rec.downloadUrl"
                                           target="_blank"
                                           prepend-icon="mdi-download"
                                           size="small">Download</v-btn>
                                    <v-btn v-if="rec.transcription || rec.summary"
                                           @click="viewTranscription(rec)"
                                           prepend-icon="mdi-text-box-outline"
                                           size="small">Transcript</v-btn>
                                    <v-chip v-else-if="!rec.downloadUrl" size="x-small" color="grey-lighten-2">No transcript</v-chip>
                                </v-card-actions>
                            </v-card>
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>
        </v-dialog>

        <v-dialog v-model="transcriptionDialog" max-width="700" scrollable>
            <v-card v-if="selectedRecording">
                <v-toolbar color="primary" dark>
                    <v-toolbar-title class="text-body-2">
                        {{ selectedRecording.callerName || selectedRecording.callerNumber }}
                        &rarr;
                        {{ selectedRecording.destinationName || selectedRecording.destinationNumber }}
                    </v-toolbar-title>
                    <v-spacer></v-spacer>
                    <v-btn icon @click="transcriptionDialog = false">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-toolbar>
                <v-card-text>
                    <p class="text-caption text-grey mb-4">
                        {{ $dayjs(selectedRecording.startTime).format('DD/MM/YYYY HH:mm') }}
                        &nbsp;·&nbsp;
                        {{ formatDuration(selectedRecording.duration) }}
                    </p>
                    <div v-if="selectedRecording.summary" class="mb-4">
                        <p class="font-weight-bold mb-1">Summary</p>
                        <p>{{ selectedRecording.summary }}</p>
                    </div>
                    <v-divider v-if="selectedRecording.summary && selectedRecording.transcription" class="mb-4"></v-divider>
                    <div v-if="selectedRecording.transcription">
                        <p class="font-weight-bold mb-1">Transcription</p>
                        <p style="white-space: pre-line;">{{ selectedRecording.transcription }}</p>
                    </div>
                </v-card-text>
            </v-card>
        </v-dialog>
    </div>
</template>
<style scoped>
    .min-width-0 {
        min-width: 0;
    }
</style>
<script>
    import { mapActions } from "vuex";

    function todayStr() {
        return new Date().toISOString().split('T')[0];
    }
    function daysAgoStr(n) {
        const d = new Date();
        d.setDate(d.getDate() - n);
        return d.toISOString().split('T')[0];
    }

    function initialState() {
        return {
            show: false,
            loading: false,
            recordings: [],
            startDate: daysAgoStr(7),
            endDate: todayStr(),
            searchTerm: '',
            transcriptionDialog: false,
            selectedRecording: null,
        };
    }

    export default {
        name: "PhoneRecordings",
        data() {
            return initialState();
        },
        methods: {
            ...mapActions(["showErrorSnack"]),
            open() {
                this.show = true;
                if (this.recordings.length === 0) {
                    this.loadData();
                }
            },
            loadData() {
                this.loading = true;
                this.$api.get('phonerecording/getrecordings', {
                    params: {
                        startDate: this.startDate,
                        endDate: this.endDate,
                        searchTerm: this.searchTerm || undefined,
                    }
                }).then(res => {
                    this.recordings = res.data;
                    this.loading = false;
                }).catch(() => {
                    this.loading = false;
                    this.showErrorSnack("Failed to load phone recordings.");
                });
            },
            onClearSearch() {
                this.searchTerm = '';
                this.loadData();
            },
            viewTranscription(rec) {
                this.selectedRecording = rec;
                this.transcriptionDialog = true;
            },
            formatDuration(seconds) {
                if (!seconds) return '0:00';
                const m = Math.floor(seconds / 60);
                const s = seconds % 60;
                return `${m}:${s.toString().padStart(2, '0')}`;
            },
        },
    };
</script>
